/* eslint-disable react/jsx-one-expression-per-line */
import { ReactElement, useEffect, useState, Fragment } from 'react';
import { useFormikContext, Field, getIn } from 'formik';
import { navigateToError } from '@utils';
import { FormType } from '@constants/forms';
import { Alert, MultiTextInput, TileRadio, ExpandableTableAccessible } from '@components';
import { iObligation, tile } from '@interfaces/interfaces';
import { iFVSFormValues } from '@services/FVSService';
import { getObligationOptions } from '@api';
import classnames from 'classnames';
import InfoIcon from '../../../../images/info.svg';
import styles from './FVSStep9.module.scss';

function FVSStep9(): ReactElement {
  const [loading, setLoading] = useState(false);
  const context = useFormikContext();
  const { values, touched }: { values: iFVSFormValues; touched: any } = useFormikContext();
  const [authorisedOb, setObligationDetails] = useState({} as iObligation);
  const [relevantObs, setRelevantObligations] = useState([] as iObligation[]);
  const hasTouchedWhichOtherFines = getIn(touched, 'WhichOtherFines ');
  const FVSStep9AlertContent = (): ReactElement => (
    <p>
      If any of your fines are on a payment plan with an enforcement agency (eg. Victoria Police, Public Transport Victoria) you should contact them directly to cancel the plan –
      if you pay the fine in full we cannot consider it in your application.
    </p>
  );
  const YesValue = (): ReactElement => (
    <>
      <div>Yes</div>
    </>
  );

  const NoValue = (): ReactElement => (
    <>
      <div>No</div>
    </>
  );
  const TILE_DATA: tile[] = [
    {
      body: <YesValue />,
      value: 'true',
    },
    {
      body: <NoValue />,
      value: 'false',
    },
  ];
  const TILE_DATA_OTHER_FINES: tile[] = [
    {
      body: 'All eligible outstanding fines',
      value: 'AllFines',
    },
    {
      body: 'Some eligible outstanding fines (select from list below)',
      value: 'SomeFines',
    },
    {
      body: 'No additional fines',
      value: 'NoFines',
    },
  ];
  useEffect(() => {
    if (values.AdditionalFinesToInclude === 'false') {
      context.setFieldValue('AgencyNames', [{ value: '' }]);
      context.setFieldValue('InfringementNums', [{ value: '' }]);
      context.setFieldValue('AOffence', [{ value: '' }]);
      context.setFieldValue('DAOffence', [{ value: '' }]);
      context.setFieldTouched('AgencyNames');
      context.setFieldTouched('InfringementNums');
      context.setFieldTouched('AOffence');
      context.setFieldTouched('DAOffence');
    }
    try {
      const obNumFormIk = values.OBLIGATION_NUMBER;
      getObligationOptions(obNumFormIk, FormType.FAMILYVIOLENCESCHEME)
        .then((responseBody) => {
          if (responseBody && responseBody?.Authorised) {
            setObligationDetails(responseBody?.Authorised);
            context.setFieldValue('AuthorisedObligation', responseBody?.Authorised);
            context.setFieldValue('InfringementNoticeNumber', responseBody?.Authorised.InfringementNumber);
          }
          if (responseBody && responseBody?.Obligations) {
            setRelevantObligations(responseBody?.Obligations);
            context.setFieldValue('relevantObs', responseBody?.Obligations);
            context.setFieldValue('EligibleObligations', responseBody?.Obligations);
          }
        })
        .catch((e) => console.log(e));
      setLoading(false);
    } catch (ex) {
      navigateToError();
    }
    return () => {
      setLoading(false);
    };
  }, [values.OBLIGATION_NUMBER, values.AdditionalFinesToInclude]);

  return (
    <>
      {!loading && (
        <>
          <div className="vfH2">Your fines</div>
          <p>You are applying to the Family Violence Scheme for the following fine:</p>
          <div className={styles.offenceDetails}>
            <div className={styles.detailHeadings}>
              <span>OBLIGATION NUMBER</span>
              <span>OFFENCE DATE</span>
              <span>OFFENCE DESCRIPTION</span>
              <span>AMOUNT</span>
            </div>
            <div className={styles.detailValues}>
              <span className={styles.obligationNumber}>{authorisedOb.Number}</span>
              <span>{authorisedOb.OffenceDate}</span>
              <span>{authorisedOb.OffenceDesc}</span>
              <span>${authorisedOb.AmountDue}</span>
            </div>
            <div className={styles.detailTable}>
              <table className={styles.offenceTable}>
                <tbody>
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>OBLIGATION NUMBER</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.Number}</td>
                  </tr>
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>OFFENCE DATE</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.OffenceDate}</td>
                  </tr>
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>OFFENCE DESCRIPTION</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.OffenceDesc}</td>
                  </tr>
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>AMOUNT</td>
                    <td className={styles.tdSecondColBackground}>${authorisedOb.AmountDue}</td>
                  </tr>
                  <tr className={styles.hideInMobileView}>
                    <td>OFFENCE LOCATION</td>
                    <td className={styles.uppcaseText}>{authorisedOb.OffenceLocation}</td>
                  </tr>
                  <tr className={styles.hideInMobileView}>
                    <td>TIME OF OFFENCE</td>
                    <td>{authorisedOb.OffenceTime}</td>
                  </tr>
                  <tr className={styles.hideInMobileView}>
                    <td>VEHICLE REGISTRATION</td>
                    <td>{authorisedOb.Registration}</td>
                  </tr>
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>OFFENCE LOCATION <br /> <span className={styles.tdSpanFont}>{authorisedOb.OffenceLocation}</span></td>
                  </tr>
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>TIME OF OFFENCE <br /> <span className={styles.tdSpanFont}>{authorisedOb.OffenceTime}</span></td>
                  </tr>
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>VEHICLE REGISTRATION <br /> <span className={styles.tdSpanFont}>{authorisedOb.Registration}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            {relevantObs.length !== 0 && (
              <>
                <div
                  className={classnames({
                    vfH3: true,
                    [styles.titleError]: hasTouchedWhichOtherFines && !values.WhichOtherFines,
                  })}>
                  Which other fines do you want to include in the application?
                </div>
                <Field name="WhichOtherFines" classname={styles.tile} component={TileRadio} tileData={TILE_DATA_OTHER_FINES} displayError={false} orientationRow={false} isMandatory />
                <div
                  className={classnames({
                    vfH3: true,
                    [styles.titleError]: hasTouchedWhichOtherFines && !values.WhichOtherFines,
                  })}>
                  Eligible outstanding fines
                </div>
                {values.WhichOtherFines === 'AllFines' && (
                  <Field
                    name="SelectedObligationsStep9"
                    key="eligible"
                    obligations={relevantObs}
                    inactiveRows
                    component={ExpandableTableAccessible}
                    selectedRows
                    selectAllButtonAtBottom
                  />
                )}
                {values.WhichOtherFines === 'SomeFines' && (
                  <Field
                    name="SelectedObligationsStep9"
                    key="eligible"
                    obligations={relevantObs}
                    inactiveRows={false}
                    component={ExpandableTableAccessible}
                    selectedRows={false}
                    selectAllButtonAtBottom
                  />
                )}
                {values.WhichOtherFines !== 'AllFines' && values.WhichOtherFines !== 'SomeFines' && (
                  <Field
                    name="SelectedObligationsStep9"
                    key="eligible"
                    obligations={relevantObs}
                    inactiveRows
                    component={ExpandableTableAccessible}
                    selectedRows={false}
                    selectAllButtonAtBottom
                  />
                )}
                <p className={styles.middleReferenceSection}>
                  This list does not include fines issued by a court, excessive speeding, drug-driving or drink-driving fines. These fines are not eligible for the Family Violence Scheme.
                </p>
              </>
            )}
            <br />
            <br />
            <div className={styles.title}>Infringement stage fines</div>
            <p className={styles.text}>
              FVS may be able to consider other fines that are not listed above (for example, parking fines issued by a council or a fine you received for not voting). However we
              require information from you to identify these fines.
            </p>
            <div>
              <Field
                name="AdditionalFinesToInclude"
                title="Are there other fines, not listed above, that you would like to include in the application? "
                component={TileRadio}
                tileData={TILE_DATA}
                orientationRow
              />
            </div>
            {values.AdditionalFinesToInclude === 'true' && (
              <MultiTextInput name="enteredObligationsArray" formName={FormType.FAMILYVIOLENCESCHEME} stepNo="step9" includeUnregisteredFines />
            )}
            {values.AdditionalFinesToInclude === 'true' && <Alert message={<FVSStep9AlertContent />} theme="info" title="Please note:" icon={InfoIcon} />}
          </div>
        </>
      )}
    </>
  );
}

export default FVSStep9;
